import React, { useEffect, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormHelperText,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { ContainerList, ProductionTypeCategoryList } from "../../utils";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/20/solid";
import { CheckCircleIcon, XMarkIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import { NumberField, theme } from ".";
import { ArrowDownward, ArrowUpward, History, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import { UserPermisions } from ".";

function isValidJsonString(str) {
  try {
    if (str !== null)
      JSON.parse(str);
    else return false;
  } catch (e) {
    return false;
  }
  return true;
}

const FlavorSorting = ({ sortBy = "", setSortBy }) => {
  return <>
    {sortBy === 'alpha-asc' ?
      <IconButton title="Z-A" size="small" color="inherit" onClick={() => setSortBy('alpha-desc')}><ArrowUpward fontSize="small" /></IconButton>
      : sortBy === 'alpha-desc' ?
        <IconButton title="A-Z" size="small" color="inherit" onClick={() => setSortBy('recent')}><ArrowDownward fontSize="small" /></IconButton>
        : <IconButton title="Recent" size="small" color="inherit" onClick={() => setSortBy('alpha-asc')}><History fontSize="small" /></IconButton>
    }
  </>
};

const EditableTableCell = ({ row, i, field, editable = false, onChange = () => { }, tourClassNames = "" }) => {
  return (
    <>
      <TableCell align="center" className={`border-[1px] ${tourClassNames}`}>
        {editable ?
          <NumberField
            name={field}
            value={row?.[field]}
            onChange={(e) => onChange(e, field, i)}
            error={row?.errors?.[field]}
            required
          />
          : row?.[field]}
      </TableCell>
    </>
  )

}

const errorMessages = {
  emptyError: 'Input cannot be empty.',
  invalidDecimalError: 'Invalid input. Must be an integer or a float with digits after the decimal point being 125, 25, 375, 50, 625, 75, 875, 0, or 00.',
  negativeError: 'Please enter valid number',
  deliveredError: 'Delivered should not be more than (start + made)',
  throwError: 'Please enter valid throw',
  leftOverError: 'Left should not be more than total'
}

const CategoryCard = (props) => {
  const { dataItem, handleDelete, handleUpdate, handleBulkUpdate, getContainerValue, canSubmit, selectedDate, calculateValues, setIsEditing, sortBy, setSortBy, bulkEdit = false } = props;

  const { enqueueSnackbar } = useSnackbar();
  const [editableRow, setEditableRow] = useState('');
  const [expandedRow, setExpandedRow] = useState('');
  const [flavorsList, setFlavorsList] = useState([]);
  const [showDeleteCheckboxes, setShowDeleteCheckboxes] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [openMenuIndex, setOpenMenuIndex] = useState(-1);
  const openMenu = Boolean(anchorEl);
  const data = useSelector((state) => state);
  const inputPattern = /^\d*\.?((125)|(25)|(375)|(50)|(5)|(625)|(75)|(875)|(0)|(00))?$/; // Input regexp patter for decimal values

  const handleAddClick = (event, i) => {
    setAnchorEl(event.currentTarget);
    setOpenMenuIndex(i);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setOpenMenuIndex(-1);
  };

  const ContainerName = ProductionTypeCategoryList?.find(
    (i) => i?.intProductTypeID === dataItem?.intProductTypeID
  )?.product_type_name;
  // let flavorsList = dataItem?.flavorsList?.filter((i) => !i?.isDelete);

  useEffect(() => {
    if (dataItem?.length) {
      const flavorsListCopy = JSON.parse(JSON.stringify(dataItem));
      setFlavorsList(flavorsListCopy.filter((i) => !i?.isDelete));
    }
    else setFlavorsList([]);
  }, [dataItem]);

  useEffect(() => {
    setExpandedRow('');
    setShowDeleteCheckboxes('');
  }, [selectedDate]);

  const handleDeleteIconClick = (i) => {
    setExpandedRow(i);
    setShowDeleteCheckboxes(i);
  };

  const handleCancelDelete = (i) => {
    setExpandedRow('');
    setShowDeleteCheckboxes('');
    const flavorsListCopy = [...flavorsList];
    flavorsListCopy[i].containers = JSON.stringify(JSON.parse(flavorsListCopy[i].containers).map((c) => ({ ...c, checked: false })));
    setFlavorsList(flavorsListCopy);
  }

  const handleConfirmDelete = (i, row, containers, selectedContainers) => {
    if (containers.length === selectedContainers.length) {
      handleDelete(row);
      setShowDeleteCheckboxes('');
      setExpandedRow('');
    }
    else {
      const flavorsListCopy = [...flavorsList];
      const unCheckedContainers = JSON.parse(flavorsListCopy[i].containers).filter((c) => !c.checked);
      flavorsListCopy[i].containers = JSON.stringify(unCheckedContainers);
      flavorsListCopy[i].made = unCheckedContainers?.reduce((acc, cur) => acc + getContainerValue(cur.id), 0);
      const { total, sold } = calculateValues(flavorsListCopy[i]);
      flavorsListCopy[i].total = total;
      flavorsListCopy[i].sold = sold;
      flavorsListCopy[i].isUpdate = true;
      setFlavorsList(flavorsListCopy);
      handleUpdate(i, flavorsListCopy[i]);
      setShowDeleteCheckboxes('');
      setExpandedRow('');
    }
  }

  const handleSelectAllClick = (e, i, containers) => {
    const flavorsListCopy = [...flavorsList];
    flavorsListCopy[i].containers = JSON.stringify(containers.map((c) => ({ ...c, checked: e.target.checked })));
    setFlavorsList(flavorsListCopy);
  }

  const handleSelectCheckbox = (e, i, containers, containerIndex) => {
    const flavorsListCopy = [...flavorsList];
    containers[containerIndex].checked = e.target.checked;
    flavorsListCopy[i].containers = JSON.stringify(containers);
    setFlavorsList(flavorsListCopy);
  }

  const handleUpdateClick = (i, rowData) => {
    const errors = {};
    if (isNaN(parseFloat(rowData.delivered)) || parseFloat(rowData.delivered) < 0)
      errors.delivered = "Please enter a delivered.";
    if (isNaN(parseFloat(rowData.returned)) || parseFloat(rowData.returned) < 0)
      errors.returned = "Please enter a returned.";
    if (isNaN(parseFloat(rowData.throw)) || parseFloat(rowData.throw) < 0)
      errors.throw = "Please enter a throw.";
    if (isNaN(parseFloat(rowData.leftOver)) || parseFloat(rowData.leftOver) < 0)
      errors.leftOver = "Please enter a leftOver.";
    if (Object.keys(errors)?.length === 0) {
      const newRow = { ...rowData, isUpdate: true }
      handleUpdate(i, newRow);
      setEditableRow('');
      setIsEditing(false);
    }
    else {
      const flavorsListData = [...flavorsList];
      if (Object.keys(errors).length > 0)
        flavorsListData[i].errors = errors;
      setFlavorsList([...flavorsListData]);
    }
  }

  const handleAddBatch = (i, rowData, container) => {
    rowData.containers = JSON.stringify([...(JSON.parse(rowData?.containers)), { id: container.id, date: new Date().toISOString() }]);
    rowData.made += container.value;
    const { total, sold } = calculateValues(rowData);
    rowData.total = total;
    rowData.sold = sold;
    rowData.isUpdate = true;
    handleUpdate(i, rowData);
    handleClose();
    enqueueSnackbar("Batch added Successfully", {
      anchorOrigin: { vertical: "top", horizontal: "right" },
      variant: "success",
    });
  }

  const handleCancelEdit = (i) => {
    const flavorsListCopy = JSON.parse(JSON.stringify(dataItem));
    setFlavorsList(flavorsListCopy?.filter((i) => !i?.isDelete));
    setEditableRow('');
    setIsEditing(false);
    // setBulkEdit(false);
  }

  const fixedToThreeDecimals = (number) => {
    if (number.toString().split('.')[1] && number.toString().split('.')[1].length > 2) {
      return number.toFixed(3);
    } else {
      return number;
    }
  }

  const checkForReturned = (flavorsListDataItem) => {
    return (inputPattern.test(parseFloat(flavorsListDataItem.returned || 0)));
  }

  const checkForDelivered = (flavorsListDataItem) => {
    return ((parseFloat(flavorsListDataItem.start || 0) + parseFloat(flavorsListDataItem.made || 0)) >= parseFloat(flavorsListDataItem.delivered || 0)) && inputPattern.test(parseFloat(flavorsListDataItem.delivered || 0));
  }

  const checkForLeftOver = (flavorsListDataItem) => {
    return (flavorsListDataItem.total > parseFloat(flavorsListDataItem.leftOver || 0)) && inputPattern.test(parseFloat(flavorsListDataItem.leftOver || 0));
  }

  const checkForThrow = (flavorsListDataItem) => {
    return (flavorsListDataItem.throw < ((parseFloat(flavorsListDataItem.start || 0) + parseFloat(flavorsListDataItem.made || 0) + parseFloat(flavorsListDataItem.returned || 0)) - parseFloat(flavorsListDataItem.delivered || 0))) && inputPattern.test(parseFloat(flavorsListDataItem.throw))
  }

  const calcTotalAndSoldOfFlv = (flavorData = {}) => {
    const total =
      parseFloat(flavorData.start || 0) +
      parseFloat(flavorData.made || 0) +
      parseFloat(flavorData.returned || 0) -
      parseFloat(flavorData.delivered || 0) -
      parseFloat(flavorData.throw || 0);
    const sold = parseFloat(total || 0) - parseFloat(flavorData.leftOver || 0);
    return { total, sold }
  }

  const handleInputChange = (e, name, i) => {
    const value = parseFloat(e.target.value || 0);
    if (value < 0) return null;
    const flavorsListData = [...flavorsList];
    flavorsListData[i][name] = value;
    const { total, sold } = calcTotalAndSoldOfFlv(flavorsListData[i])
    flavorsListData[i].total = fixedToThreeDecimals(total);
    flavorsListData[i].sold = fixedToThreeDecimals(sold);
    flavorsListData[i].isUpdate = true;
    const errors = flavorsListData[i]?.errors ? { ...flavorsListData[i].errors } : {};
    if (value === "") {
      errors[name] = errorMessages?.emptyError;
    } else {
      if (name === 'delivered') {
        if (!inputPattern.test(value))
          errors[name] = errorMessages?.invalidDecimalError
        else if (value > (parseFloat(flavorsListData[i].start || 0) + parseFloat(flavorsListData[i].made || 0)))
          errors[name] = errorMessages?.deliveredError
        else if (flavorsListData[i].total < 0 || flavorsListData[i].sold < 0)
          errors[name] = errorMessages?.negativeError
        else
          delete errors[name];
        if (checkForReturned(flavorsListData[i]))
          delete errors['returned'];
        if (checkForThrow(flavorsListData[i]))
          delete errors['throw'];
        if (checkForLeftOver(flavorsListData[i]))
          delete errors['leftOver'];
      }
      if (name === 'returned') {
        if (!inputPattern.test(value))
          errors[name] = errorMessages?.invalidDecimalError
        else if (flavorsListData[i].total < 0 || flavorsListData[i].sold < 0)
          errors[name] = errorMessages?.negativeError
        else
          delete errors[name];
        if (checkForDelivered(flavorsListData[i]))
          delete errors['delivered'];
        if (checkForThrow(flavorsListData[i]))
          delete errors['throw'];
        if (checkForLeftOver(flavorsListData[i]))
          delete errors['leftOver'];
      }
      if (name === 'throw') {
        if (!inputPattern.test(value))
          errors[name] = errorMessages?.invalidDecimalError
        else if (value > ((parseFloat(flavorsListData[i].start || 0) + parseFloat(flavorsListData[i].made || 0) + parseFloat(flavorsListData[i].returned || 0)) - parseFloat(flavorsListData[i].delivered || 0)))
          errors[name] = errorMessages?.throwError
        else if (flavorsListData[i].total < 0 || flavorsListData[i].sold < 0)
          errors[name] = errorMessages?.negativeError
        else
          delete errors[name];
        if (checkForDelivered(flavorsListData[i]))
          delete errors['delivered'];
        if (checkForReturned(flavorsListData[i]))
          delete errors['returned'];
        if (checkForLeftOver(flavorsListData[i]))
          delete errors['leftOver'];
      }
      if (name === 'leftOver') {
        if (!inputPattern.test(value))
          errors[name] = errorMessages?.invalidDecimalError
        else if (flavorsListData[i].total < 0 || flavorsListData[i].sold < 0)
          errors[name] = errorMessages?.negativeError
        else if (value > flavorsListData[i].total)
          errors[name] = errorMessages?.leftOverError
        else delete errors[name];
        if (checkForDelivered(flavorsListData[i]))
          delete errors['delivered'];
        if (checkForReturned(flavorsListData[i]))
          delete errors['returned'];
        if (checkForThrow(flavorsListData[i]))
          delete errors['throw'];
      }
    }

    if (Object.keys(errors).length > 0) {
      flavorsListData[i].errors = errors;
    } else {
      flavorsListData[i].errors = {};
    }
    setFlavorsList([...flavorsListData]);
    handleBulkUpdate([...flavorsListData]);
  };

  if (flavorsList?.length === 0) {
    return <></>;
  }

  return (
    <>
      <ThemeProvider theme={theme}>
        <TableHead className="sticky top-0 z-10" >
          <TableRow className="bg-[#D41042]">
            <TableCell align="center" width={50} className="text-white  p-[7px]"></TableCell>
            <TableCell align="center" width={80} className="text-white  p-[7px]">Container</TableCell>
            <TableCell
              // width={250}
              // sx={{ width: '250px', maxWidth: '250px' }}
              align="left"
              className="text-white border-[1px] p-[7px] flex items-center justify-between"
            >
              Flavor
              <FlavorSorting sortBy={sortBy} setSortBy={setSortBy} />
            </TableCell>
            {/* <TableCell
                  align="left"
                  className="text-white border-[1px] p-[7px]"
                >
                  Quantity
                </TableCell> */}
            <TableCell
              align="center"
              className="text-white border-[1px] p-[7px]"
            >
              Start
            </TableCell>
            <TableCell
              align="center"
              className="text-white border-[1px] p-[7px]"
            >
              Made
            </TableCell>
            <TableCell
              align="center"
              className="text-white border-[1px] p-[7px]"
            >
              Delivered
            </TableCell>
            <TableCell
              align="center"
              className="text-white border-[1px] p-[7px]"
            >
              Return
            </TableCell>
            <TableCell
              align="center"
              className="text-white border-[1px] p-[7px]"
            >
              Throw
            </TableCell>
            <TableCell
              align="center"
              className="text-white border-[1px] p-[7px]"
            >
              Total
            </TableCell>
            <TableCell
              align="center"
              className="text-white border-[1px] p-[7px]"
            >
              Left
            </TableCell>
            <TableCell
              align="center"
              className="text-white border-[1px] p-[7px]"
            >
              Sold
            </TableCell>
            <TableCell align="center" className="text-white  p-[7px]">
              Action{" "}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody sx={{ borderBottom: '8px solid #e5e5e5' }} className="my-2 bg-white border-bottom-md">
          {flavorsList.map((row, i) => {
            const selectedContainers = [];
            const containers = isValidJsonString(row?.containers) ?
              JSON.parse(row?.containers)?.map(obj => {
                if (obj.checked) selectedContainers.push(obj);
                const container = ContainerList?.find((i) => obj?.id === i?.id);
                return ({
                  ...obj, text: container?.text, name: container?.name, shortName: container?.name.split(" ").map((name) => name.charAt(0)).join("")
                })
              })
              : [];
            return (
              <>
                <TableRow
                  sx={{
                    '&:nth-of-type(odd)': {
                      backgroundColor: '#FFF8FA',
                    },
                    backgroundColor: expandedRow === i && '#F3FFF4 !important'
                  }}
                  key={`${row?.flavor?.flavorID}-${row?.containerID}-${row?.intProductTypeID}-${i}`}
                  className={`${(editableRow !== '' && editableRow !== i) && 'opacity-25 pointer-events-none'}`}
                >
                  <TableCell scope="row" align="left" className="border-[1px]">
                    {showDeleteCheckboxes === i ? <Checkbox
                      color="primary"
                      indeterminate={selectedContainers.length > 0 && selectedContainers.length < containers.length}
                      checked={(containers.length > 0 && selectedContainers.length === containers.length)}
                      onChange={(e) => handleSelectAllClick(e, i, containers)}
                    /> : <IconButton
                      aria-label="expand row"
                      size="small"
                      onClick={() => setExpandedRow(expandedRow === i ? '' : i)}
                      style={{ visibility: containers.length > 1 ? 'visible' : 'hidden' }}
                    >
                      {(expandedRow === i) ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>}
                  </TableCell>
                  <TableCell scope="row" align="left" className="border-[1px]">
                    <span>{Array.from(new Set(containers.map(c => c.shortName)))?.join(',')}</span>
                  </TableCell>
                  <TableCell sx={{ maxWidth: '225px' }} align="left" className="border-[1px] p-1">
                    <span className="flex justify-between items-center">
                      {row?.flavor?.image_url && <Avatar
                        title={row?.flavor?.flavor_name}
                        sx={{
                          width: 30, height: 30
                        }}
                        style={{ marginRight: "10px" }}
                        alt=""
                        src={row?.flavor?.image_url}
                      />}
                      {!row?.flavor?.image_url && <Avatar
                        title={row?.flavor?.flavor_name}
                        sx={{
                          width: 28, height: 28
                        }}
                        style={{
                          background: "#fff",
                          border: "2px solid grey",
                          marginRight: "10px"
                        }}
                      > </Avatar>}
                      <span title={row?.flavor?.flavor_name} className="flavor-text">{row?.flavor?.flavor_name}<br />
                        <span className="text-xs font-bold ml-[3px]">{ProductionTypeCategoryList.find(p => p.intProductTypeID === row?.intProductTypeID).product_type_name}</span>
                      </span>
                      {containers.length === 1 ? <IconButton
                        title="Recipe Card"
                        disabled={!row?.flavor[containers[0]?.text]}
                        disableTouchRipple
                        sx={{ width: 24, height: 24, padding: 0, margin: "0 5px 0 auto", opacity: !row?.flavor[containers[0]?.text] && .5, visibility: containers.length === 1 ? 'visible' : 'hidden' }}
                        onClick={(e) => props?.showImagePDF(row?.flavor[containers[0]?.text])}
                      >
                        <img
                          className="image"
                          src="../../assets/icons/recipe-card-icon.png"
                          alt="no image"
                        />
                      </IconButton> : <span style={{ width: 20, margin: '0 5px 0 auto' }}>({containers.length})</span>}
                    </span>
                  </TableCell>
                  <TableCell align="center" className="border-[1px] ps-start-tour-selector">
                    {row?.start}
                  </TableCell>
                  <TableCell align="center" className="border-[1px] ps-made-tour-selector">
                    {row?.made}
                  </TableCell>
                  <EditableTableCell row={row} i={i} field="delivered" editable={editableRow === i || bulkEdit} onChange={handleInputChange} tourClassNames="ps-delivered-tour-selector" />
                  <EditableTableCell row={row} i={i} field="returned" editable={editableRow === i || bulkEdit} onChange={handleInputChange} tourClassNames="ps-returned-tour-selector" />
                  <EditableTableCell row={row} i={i} field="throw" editable={editableRow === i || bulkEdit} onChange={handleInputChange} tourClassNames="ps-throw-tour-selector" />
                  <TableCell align="center" className="border-[1px] ps-total-tour-selector">
                    {row?.total}
                  </TableCell>
                  <EditableTableCell row={row} i={i} field="leftOver" editable={editableRow === i || bulkEdit} onChange={handleInputChange} />
                  <TableCell align="center" className="border-[1px] ps-sold-tour-selector">
                    {row?.sold}
                  </TableCell>
                  <TableCell align="center" className="ps-addEditData-tour-selector">
                    {(UserPermisions[data?.profile?.role]?.submit && canSubmit(selectedDate) && !bulkEdit) && <div className="flex justify-around">
                      {editableRow === i ?
                        <>
                          <IconButton disabled={row?.errors && Object.keys(row?.errors)?.length !== 0} onClick={() => handleUpdateClick(i, row)}>
                            <CheckCircleIcon
                              fontSize={'1rem'}
                              className="w-5 text-grey-500 cursor-pointer"
                            />
                          </IconButton>
                          <IconButton onClick={() => handleCancelEdit(i, row)}>
                            <XMarkIcon
                              className="w-5 text-grey-500 cursor-pointer"
                            />
                          </IconButton>
                        </>
                        :
                        [showDeleteCheckboxes === i ?
                          <>
                            <Link color="#292929" component="button" onClick={() => handleCancelDelete(i)}>
                              Cancel
                            </Link>
                            <Link color="#B72C00" className="ml-2" component="button" onClick={() => handleConfirmDelete(i, row, containers, selectedContainers)}>
                              Delete
                            </Link>
                          </>
                          : <>
                            <IconButton disabled={props?.checkForResubmit()?.classValue} onClick={(e) => handleAddClick(e, i)}>
                              <PlusCircleIcon
                                className={`h-4 w-4 text-grey-500`}
                              />
                            </IconButton>
                            {openMenu && <Menu
                              id="basic-menu"
                              anchorEl={anchorEl}
                              open={openMenu && openMenuIndex === i}
                              onClose={handleClose}
                              MenuListProps={{
                                'aria-labelledby': 'basic-button',
                              }}
                            >
                              {ContainerList.map((c, index) => {
                                const recipeCard = row?.flavor?.[c?.id === 1 ? 'quarter_container_recipe' : c?.id === 2 ? 'half_container_recipe' : c?.id === 3 ? 'single_container_recipe' : 'double_container_recipe'];
                                return <MenuItem key={index} disabled={!recipeCard} onClick={() => handleAddBatch(i, row, c)}> <div className="mr-3">{c.name.split(' ').shift()}</div>
                                </MenuItem>
                              })}
                            </Menu>}
                            {containers.length > 0 && <IconButton disabled={props?.checkForResubmit()?.classValue} onClick={() => containers.length <= 1 ? handleDelete(row) : handleDeleteIconClick(i)}>
                              <TrashIcon
                                className={`h-4 w-4 text-grey-500`}
                              />
                            </IconButton>}
                          </>]}
                    </div>}
                  </TableCell>
                </TableRow>
                {(containers.length > 1 && expandedRow === i) && containers?.map((container, index) => <TableRow key={index} sx={{ backgroundColor: '#F3FFF4' }} style={{ display: expandedRow === i ? '' : 'none', maxHeight: 0 }} >
                  <TableCell className="border-[1px]" colSpan={1}>{showDeleteCheckboxes === i && <Checkbox
                    color="primary"
                    checked={container.checked || false}
                    onChange={(e) => handleSelectCheckbox(e, i, containers, index)}
                  />}</TableCell>
                  <TableCell align="center" className="border-[1px]" colSpan={1}>
                    {container?.name?.split(' ').shift()}
                  </TableCell>
                  <TableCell align="center" className="border-[1px]" colSpan={9}>
                    Batch time ({container.date ? new Date(container.date).toLocaleString([], { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'})
                  </TableCell>
                  <TableCell align="center" className="border-[1px]" colSpan={1}>
                    <IconButton
                      title="Recipe Card"
                      disabled={!row?.flavor[container?.text]}
                      disableTouchRipple
                      sx={{ width: 24, height: 24, padding: 0, margin: "0 5px 0 auto", opacity: !row?.flavor[container?.text] && .5 }}
                      onClick={(e) => props?.showImagePDF(row?.flavor[container?.text])}
                    >
                      <img
                        className="image"
                        src="../../assets/icons/recipe-card-icon.png"
                        alt="no image"
                      />
                    </IconButton>
                  </TableCell>
                </TableRow>)}
              </>
            );
          })}
        </TableBody>
      </ThemeProvider>
    </>
  );
};

export default CategoryCard;
